import { RenderingEngineContext } from "./components/RenderingEngineContext"
import { applyEditEvents } from "@tscircuit/core"
import { findBoundsAndCenter } from "@tscircuit/circuit-json-util"
import type { AnyCircuitElement, SourceTrace } from "circuit-json"
import { ContextProviders } from "./components/ContextProviders"
import type { StateProps } from "./global-store"
import type { GraphicsObject } from "graphics-debug"
import { ToastContainer } from "lib/toast"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useMeasure } from "react-use"
import { compose, scale, translate } from "transformation-matrix"
import useMouseMatrixTransform from "use-mouse-matrix-transform"
import { CanvasElementsRenderer } from "./components/CanvasElementsRenderer"
import type { BoundsSelection } from "./components/DimensionOverlay"
import type { ManualEditEvent } from "@tscircuit/props"
import { zIndexMap } from "lib/util/z-index-map"
import { calculateCircuitJsonKey } from "lib/calculate-circuit-json-key"
import { calculateBoardSizeKey } from "lib/calculate-board-size-key"

const defaultTransform = compose(translate(400, 300), scale(40, -40))

type Props = {
  circuitJson?: AnyCircuitElement[]
  height?: number
  /** Initial engine; users can switch via the context menu. Prop changes reset it.
   * WebGPU runs in a worker and falls back to Canvas when unavailable. */
  renderer?: "webgpu" | "canvas"
  allowEditing?: boolean
  editEvents?: ManualEditEvent[]
  initialState?: Partial<StateProps>
  onEditEventsChanged?: (editEvents: ManualEditEvent[]) => void
  onBoundsSelected?: (bounds: BoundsSelection) => void
  focusOnHover?: boolean
  clickToInteractEnabled?: boolean
  debugGraphics?: GraphicsObject | null
  disablePcbGroups?: boolean
}

export const PCBViewer = ({
  circuitJson,
  debugGraphics,
  height = 600,
  renderer = "webgpu",
  initialState,
  allowEditing = true,
  editEvents: editEventsProp,
  onEditEventsChanged,
  onBoundsSelected,
  focusOnHover = false,
  clickToInteractEnabled = false,
  disablePcbGroups = false,
}: Props) => {
  const [activeRenderer, setActiveRenderer] = useState(renderer)
  useEffect(() => setActiveRenderer(renderer), [renderer])
  const renderingEngine = useMemo(
    () => ({ renderer: activeRenderer, setRenderer: setActiveRenderer }),
    [activeRenderer],
  )
  const [isInteractionEnabled, setIsInteractionEnabled] = useState(
    !clickToInteractEnabled,
  )
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false)
  const contextMenuOpenRef = useRef(false)
  const [ref, refDimensions] = useMeasure()
  const [transform, setTransformInternal] = useState(defaultTransform)
  const shouldAllowCanvasInteraction = useCallback(
    (event: MouseEvent | TouchEvent | WheelEvent) => {
      if (contextMenuOpenRef.current) return false
      if (
        event instanceof MouseEvent &&
        (event.button === 2 || (event.buttons & 2) !== 0)
      )
        return false
      const target = event.target
      if (!(target instanceof Element)) return true

      return !target.closest("[data-toolbar-overlay]")
    },
    [],
  )

  const {
    ref: transformRef,
    setTransform,
    cancelDrag: cancelPanDrag,
  } = useMouseMatrixTransform({
    transform,
    onSetTransform: setTransformInternal,
    enabled: isInteractionEnabled && !isContextMenuOpen,
    shouldDrag: shouldAllowCanvasInteraction,
  })

  const onContextMenuOpenChange = useCallback(
    (open: boolean) => {
      contextMenuOpenRef.current = open
      if (open) cancelPanDrag()
      setIsContextMenuOpen(open)
    },
    [cancelPanDrag],
  )

  let [editEvents, setEditEvents] = useState<ManualEditEvent[]>([])
  editEvents = editEventsProp ?? editEvents

  const initialRenderCompleted = useRef(false)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const circuitJsonKey = useMemo(
    () => calculateCircuitJsonKey(circuitJson),
    [circuitJson],
  )
  const boardSizeKey = calculateBoardSizeKey(circuitJson)

  const resetTransform = () => {
    const elmBounds =
      refDimensions?.width > 0 ? refDimensions : { width: 500, height: 500 }
    const { center, width, height } = elements.some((e) =>
      e.type.startsWith("pcb_"),
    )
      ? findBoundsAndCenter(
          elements.filter((e) => e.type.startsWith("pcb_")) as any,
        )
      : { center: { x: 0, y: 0 }, width: 0.001, height: 0.001 }
    const scaleFactor =
      Math.min(
        (elmBounds.width ?? 0) / width,
        (elmBounds.height ?? 0) / height,
        100,
      ) * 0.75

    const targetTransform = compose(
      translate((elmBounds.width ?? 0) / 2, (elmBounds.height ?? 0) / 2),
      scale(scaleFactor, -scaleFactor, 0, 0),
      translate(-center.x, -center.y),
    )

    setTransform(targetTransform)
    return
  }

  useEffect(() => {
    if (!refDimensions?.width) return
    if (!circuitJson) return
    if (circuitJson.length === 0) return

    if (!initialRenderCompleted.current) {
      resetTransform()
      initialRenderCompleted.current = true
    }
  }, [circuitJson, refDimensions])

  useEffect(() => {
    if (initialRenderCompleted.current === true) {
      resetTransform()
    }
  }, [boardSizeKey])

  const pcbElmsPreEdit = useMemo(() => {
    return (
      circuitJson?.filter(
        (e: any) => e.type.startsWith("pcb_") || e.type.startsWith("source_"),
      ) ?? []
    )
  }, [circuitJsonKey])

  const elements = useMemo(() => {
    return applyEditEvents({
      circuitJson: pcbElmsPreEdit as any,
      editEvents,
    })
  }, [pcbElmsPreEdit, editEvents])

  const onCreateEditEvent = (event: ManualEditEvent) => {
    setEditEvents([...editEvents, event])
    onEditEventsChanged?.([...editEvents, event])
  }
  const onModifyEditEvent = (modifiedEvent: Partial<ManualEditEvent>) => {
    const newEditEvents: ManualEditEvent[] = editEvents.map((e) =>
      e.edit_event_id === modifiedEvent.edit_event_id
        ? ({ ...e, ...modifiedEvent } as ManualEditEvent)
        : e,
    )
    setEditEvents(newEditEvents)
    onEditEventsChanged?.(newEditEvents)
  }

  const mergedInitialState = useMemo(
    () => ({
      ...initialState,
      ...(disablePcbGroups && { is_showing_pcb_groups: false }),
    }),
    [initialState, disablePcbGroups],
  )

  return (
    <RenderingEngineContext.Provider value={renderingEngine}>
      <div
        ref={transformRef as any}
        style={{ position: "relative" }}
        onContextMenu={(event) => event.preventDefault()}
      >
        <div ref={ref as any}>
          <ContextProviders
            initialState={mergedInitialState}
            disablePcbGroups={disablePcbGroups}
          >
            <CanvasElementsRenderer
              renderer={activeRenderer}
              transform={transform}
              setTransform={setTransform}
              height={height}
              width={refDimensions.width}
              allowEditing={allowEditing}
              focusOnHover={focusOnHover}
              onBoundsSelected={onBoundsSelected}
              cancelPanDrag={cancelPanDrag}
              onContextMenuOpenChange={onContextMenuOpenChange}
              onCreateEditEvent={onCreateEditEvent}
              onModifyEditEvent={onModifyEditEvent}
              grid={{
                spacing: 1,
                view_window: {
                  left: 0,
                  right: refDimensions.width || 500,
                  top: height,
                  bottom: 0,
                },
              }}
              elements={elements as SourceTrace[]}
              debugGraphics={debugGraphics}
            />
            <ToastContainer />
          </ContextProviders>
        </div>
        {clickToInteractEnabled && !isInteractionEnabled && (
          <div
            onClick={() => {
              setIsInteractionEnabled(true)
              resetTransform()
            }}
            onTouchStart={(e) => {
              const touch = e.touches[0]
              touchStartRef.current = {
                x: touch.clientX,
                y: touch.clientY,
              }
            }}
            onTouchEnd={(e) => {
              const touch = e.changedTouches[0]
              const start = touchStartRef.current
              if (!start) return

              const deltaX = Math.abs(touch.clientX - start.x)
              const deltaY = Math.abs(touch.clientY - start.y)

              if (deltaX < 10 && deltaY < 10) {
                e.preventDefault()
                setIsInteractionEnabled(true)
                resetTransform()
              }

              touchStartRef.current = null
            }}
            style={{
              position: "absolute",
              inset: 0,
              cursor: "pointer",
              zIndex: zIndexMap.clickToInteractOverlay,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              touchAction: "pan-x pan-y pinch-zoom",
            }}
          >
            <div
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                color: "white",
                padding: "12px 24px",
                borderRadius: "8px",
                fontSize: "16px",
                pointerEvents: "none",
              }}
            >
              {typeof window !== "undefined" &&
              ("ontouchstart" in window || navigator.maxTouchPoints > 0)
                ? "Touch to Interact"
                : "Click to Interact"}
            </div>
          </div>
        )}
      </div>
    </RenderingEngineContext.Provider>
  )
}
